import { createContext, useEffect, useReducer, useState } from 'react';
import { AppAction, AppReducer, CashbackConfig, MerchantHeaderDetails, SmallPopUpCopy, getInitialState } from './app.reducer';
import useMerchantHeaderDetails from './page/util/use-merchant-header-details.hook'
import { PromoService } from './service/promo.service'
import { useUrlParamParser } from './page/util/url-param-parse.hook'
import { useLocation } from 'react-router-dom'
import { getLocalStorageKey, setLocalStorageKey } from './utils/local-stoarge.helper'
import { useFlag, useFlagsStatus } from '@unleash/proxy-client-react'
import { UnleashEnum } from './enum/unleash-enum'
import { NativeBridge } from './bridge/native.bridge'
import { isWebView } from './utils/web-view.helper'

export const AppContext = createContext<any>(getInitialState({}));

export const AppStateProvider = ({ children }: any) => {
    const transactionParamModel = useUrlParamParser();
    const { checkoutId, orderValue } = transactionParamModel;
    const { isLoaded, merchantHeaderTitle, merchantLogo, showMerchantHeader } = useMerchantHeaderDetails();
    const location: any = useLocation();
    const redeemCashback = useFlag(UnleashEnum.CASHBACK_ROLLOUT);
    const { flagsReady } = useFlagsStatus();

    const isRedeemCashBackEnabled = flagsReady && redeemCashback;

    const [state, dispatch] = useReducer(
        AppReducer,
        getInitialState({
            isLoaded,
            merchantHeaderTitle,
            merchantLogo,
            showMerchantHeader
        })
    );

    const [showAPR, setShowAPR] = useState(false);

    const {
        cashbackConfig: { cashBackDetailsLoaded = false },

    } = state;
    const updatePopUpBlocker = (popUpBlocked: boolean, smallView: boolean = false, smallPopUpCopy: SmallPopUpCopy) => {
        dispatch({
            type: AppAction.UPDATE_POP_UP_BLOCKED,
            payload: {
                popUpBlocked: popUpBlocked,
                smallView: smallView,
                smallPopUpCopy: smallPopUpCopy
            }
        });
    };

    const updateRetryFunction = (retry: () => void) => {
        dispatch({
            type: AppAction.UPDATE_RE_TRY_FUNCTION,
            payload: {
                retry: retry
            }
        });
    };

    const retryPopUpBlocker = () => {
        dispatch({
            type: AppAction.UPDATE_BLOCK_COUNT,
            payload: {}
        });
        state.retry();
    };

    const updateViewType = (viewType: string) => {
        dispatch({
            type: AppAction.UPDATE_VIEW_TYPE,
            payload: { viewType }
        });
    };

    const updateIsIframe = (isIframe: boolean) => {
        dispatch({
            type: AppAction.UPDATE_IS_IFRAME,
            payload: {
                isIframe: isIframe
            }
        });
    };

    const updateMerchantHeaderDetails = (merchantHeaderDetails: MerchantHeaderDetails) => {
        dispatch({
            type: AppAction.UPDATE_MERCHANT_HEADER_DETAILS,
            payload: {
                merchantHeaderDetails
            }
        });
    };
    
    const updateCashBackConfig = (cashbackConfig: Partial<CashbackConfig>) => {
        dispatch({
            type: AppAction.UPDATE_CASHBACK_CONFIG,
            payload: {
                cashbackConfig
            }
        });
    };

    const updateShowAPR = (status: boolean) => {
        setShowAPR(status);
    }

    const fetchCashbackDetails = async () => {
        if (['/otp', '/otp-bypass', '/otp-bypass-confirm', '/login', '/'].includes(location.pathname)) {
            return;
        }
        try {
            const { data: cashback } = await PromoService.getCashBackDetails(checkoutId!); // availableCashback
            if (cashback.usable !== undefined) {
                updateCashBackConfig({
                    ...cashback,
                    cashBackDetailsLoaded: true,
                });
            }
        } catch (error) {}
    };

    const divideCashBack = (dp: number, emi: number = 0) => {
        return {
            finalDp: dp,
            finalEmi: emi,
            totalCashBack: 0
        };
    };

    const getViewtype = async () => {
        const viewType = await NativeBridge.checkForNative();
        return isWebView(viewType) ? 'sdk': 'non_sdk'
    };

    const updateApplicableDiscount = (userState: { applicableDisc?: number; redirect: { url: string } }) => {
        const { applicableDisc = 0 } = userState;
        let userDiscount = 0;
        if (applicableDisc > 0) {
            userDiscount = applicableDisc;
        } else {
            const { applicableDiscount } = transactionParamModel;
            userDiscount = applicableDiscount as number;
        }
        dispatch({ type: AppAction.UPDATE_DISCOUNT_VALUE, payload: { applicableDiscount: userDiscount } });
    };

    const applyDiscount = (dp: number, emi: number, tenure: number, orderTotal: number) => {
        const { applicableDiscount } = state;
        if (applicableDiscount === 0) {
            return {
                dp,
                emi,
                orderTotal,
                dpDiscountApplied: false
            };
        } else if (dp > applicableDiscount) {
            return {
                dp: dp - applicableDiscount,
                emi,
                orderTotal: orderTotal - applicableDiscount,
                dpDiscountApplied: true
            };
        } else {
            return {
                dp,
                emi: Math.round(emi - applicableDiscount / tenure),
                orderTotal: orderTotal - applicableDiscount,
                dpDiscountApplied: false
            };
        }
    };

    useEffect(() => {
        if (!location?.state?.checkoutRedirection) {
            return
        }
        if (!cashBackDetailsLoaded) {
            fetchCashbackDetails();
        }
    }, [location]);

    const value = {
        popUpBlocked: state.popUpBlocked,
        blockCount: state.blockCount,
        smallView: state.smallView,
        smallPopUpCopy: state.smallPopUpCopy,
        webView: state.webView,
        isIframe: state.isIframe,
        viewType: state.viewType,
        headerDetails: state.merchantHeaderDetails,
        cashbackConfig: state.cashbackConfig,
        isRedeemCashBackEnabled,
        showAPR,
        updatePopUpBlocker,
        updateRetryFunction,
        retryPopUpBlocker,
        updateIsIframe,
        updateViewType,
        updateMerchantHeaderDetails,
        updateCashBackConfig,
        divideCashBack,
        getViewtype,
        updateShowAPR,
        applicableDiscount: state.applicableDiscount,
        updateApplicableDiscount,
        applyDiscount
    };

    return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
